import { createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "../../../utills/services"; 
import { notifyError, notifySuccess } from "../../../utills/DefaultFunction"; 
import { filter } from "lodash";
import dayjs from "dayjs"; // Make sure dayjs is imported

// Define the async thunk for fetching business units
/**
 * Asynchronous thunk action to add a dispute.
 *
 * @function
 * @name addDispute
 * @param {Object} params - The parameters for adding a dispute.
 * @param {Object} params.payload - The payload containing dispute data.
 * @param {Function} params.toggleDialog - Function to toggle the dialog visibility.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value.
 * @returns {Promise<Object>} The response data or an error object.
 * @throws Will throw an error if the API request fails.
 */
export const addDispute = createAsyncThunk(
  "dispute/addDispute",
  async ({ payload, toggleDialog }, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.post(`Dispute`, payload);
      notifySuccess(`Dispute Added Successfully! ID#${response?.data[0].id}`);
      toggleDialog();
      console.log("response", response);

      return response.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Asynchronous thunk action to add a Recon Reference.
 *
 * @function
 * @param {Object} params - The parameters for the action.
 * @param {Object} params.disputeObjects - The dispute objects to be sent in the request body.
 * @param {string} params.disputeID - The ID of the dispute.
 * @param {Function} params.toggleDialog - Function to toggle the dialog visibility.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value.
 * @returns {Promise<Object>} The response data from the API call.
 * @throws Will throw an error if the API call fails.
 */
export const addReconRefrence = createAsyncThunk(
  "dispute/addReconRefrence",
  async ({ disputeObjects, disputeID, toggleDialog }, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.post(
        `ReconReference/AddReconReference/${disputeID}`,
        disputeObjects
      );
      notifySuccess("Recon Refrence Added Successfully!");
      toggleDialog();
      console.log("response", response);

      return response.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk action to update a dispute.
 *
 * @function
 * @param {Object} params - The parameters for the updateDispute action.
 * @param {Object} params.payload - The payload containing the dispute data to be updated.
 * @param {Function} params.toggleDialog - Function to toggle the dialog visibility.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to reject the action with a value.
 * @returns {Promise<Object>} The updated dispute data.
 * @throws Will throw an error if the update operation fails.
 */
export const updateDispute = createAsyncThunk(
  "dispute/updateDispute",
  async ({ payload, toggleDialog }, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.put(`Dispute`, payload);
      notifySuccess("Dispute updated Successfully!");
      toggleDialog();
      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Approves a dispute.
 *
 * @function
 * @name approveDispute
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.payload - The payload to be sent with the request.
 * @param {Function} params.toggleDialog - Function to toggle the dialog visibility.
 * @param {string} params.disputeId - The ID of the dispute to be approved.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to reject with a value.
 * @returns {Promise<Object>} The response data from the API.
 * @throws Will throw an error if the API request fails.
 */
export const approveDispute = createAsyncThunk(
  "dispute/approveDispute",
  async ({ payload, toggleDialog, disputeId }, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.put(`Dispute/Approve/${disputeId}`, payload);
      notifySuccess("Dispute Approved Successfully!");
      toggleDialog();
      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Rejects a dispute by its ID.
 *
 * @function
 * @name rejectDispute
 * @param {string} disputeId - The ID of the dispute to be rejected.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {function} thunkAPI.rejectWithValue - Function to return a rejected action with a value.
 * @returns {Promise<Object>} The response data if the request is successful.
 * @throws Will throw an error if the request fails.
 */
export const rejectDispute = createAsyncThunk(
  "dispute/rejectDispute",
  async (disputeId, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.put(`Dispute/Reject/${disputeId}`);
      console.log("res", response);
      notifySuccess("Dispute Rejected Successfully!");

      // if (response.status === 200) {
      //   redirectComplainView();
      // }

      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Fetches the details of a dispute by its ID.
 *
 * @function
 * @name fetchDisputeDetails
 * @param {string} disputeId - The ID of the dispute to fetch details for.
 * @param {object} thunkAPI - The thunk API object.
 * @param {function} thunkAPI.rejectWithValue - Function to reject the thunk with a value.
 * @returns {Promise<object[]>} The parsed dispute details.
 * @throws Will throw an error if the API request fails.
 */
export const fetchDisputeDetails = createAsyncThunk(
  "dispute/fetchDisputeDetails",
  async (disputeId, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.get(`Dispute/Details/${disputeId}`);
      // notifySuccess("Dispute Approved Successfully!");
      const data = response?.data.data;

      const parsedData = data?.map((detail) => ({
        ...detail,
        formData: detail.formData ? JSON.parse(detail.formData) : {},
      }));

      console.log("Parsed data:", parsedData);
      // console.log('fetchDisputeDetails', response);
      return parsedData;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Asynchronous thunk action to update users.
 *
 * @function
 * @name updateUsers
 * @param {Object} params - The parameters for the update.
 * @param {Object} params.payload - The payload containing user data to update.
 * @param {string} params.email - The email of the user to update.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value.
 * @returns {Promise<Object>} The updated user data.
 * @throws Will throw an error if the update fails.
 */
export const updateUsers = createAsyncThunk(
  "userManagement/updateUsers",
  async ({ payload, email }, { rejectWithValue }) => {
    try {
      const response = await API.put(`Account/Update?email=${email}`, payload);
      notifySuccess(response.data.message);
      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.title);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Asynchronous thunk action to change the user's password.
 *
 * @function changePassword
 * @param {Object} params - The parameters for the password change.
 * @param {Object} params.payload - The payload containing the new password data.
 * @param {string} params.userId - The ID of the user whose password is being changed.
 * @param {Function} params.toggleDialog - Function to toggle the dialog visibility.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected action with a value.
 * @returns {Promise<Object>} The response data if the password change is successful.
 * @throws Will throw an error if the password change fails.
 */
export const changePassword = createAsyncThunk(
  "userManagement/changePassword",
  async ({ payload, userId, toggleDialog }, { rejectWithValue }) => {
    try {
      const response = await API.put(
        `Account/ChangeUserPassword?userId=${userId}`,
        payload
      );
      notifySuccess(response.data.data);
      toggleDialog();
      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.title);
      return rejectWithValue(error.response.data);
    }
  }
);

export const changeUserPassword = createAsyncThunk(
  "userManagement/changeUserPassword",
  async ({ payload, toggleChangePassword }, { rejectWithValue }) => {
    try {
      const response = await API.post(`Account/Changepassword`, payload);
      notifySuccess("Password changed successfully!");
      toggleChangePassword();
      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk action to fetch a dispute by its ID.
 *
 * @function
 * @param {string} id - The ID of the dispute to fetch.
 * @param {object} thunkAPI - The thunk API object.
 * @param {function} thunkAPI.rejectWithValue - Function to return a rejected action with a value.
 * @returns {Promise<object>} The dispute data if the request is successful.
 * @throws {object} The error response data if the request fails.
 */
export const getDisputeById = createAsyncThunk(
  "dispute/getDisputeById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await API.get(`Dispute/${id}`);
      //console.log("byId", response);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Asynchronous thunk action to fetch reasons for dispute.
 *
 * @function
 * @name getReasonForDispute
 * @param {string} id - The ID of the dispute.
 * @param {object} thunkAPI - The thunk API object.
 * @param {function} thunkAPI.rejectWithValue - Function to return a rejected action with a value.
 * @returns {Promise<object>} The response data containing reasons for dispute.
 * @throws Will throw an error if the request fails.
 */
export const getReasonForDispute = createAsyncThunk(
  "dispute/getReasonForDispute",
  async (id, { rejectWithValue }) => {
    try {
      const response = await API.get(`ReasonForDispute/GetAll`);
      console.log("getReasonForDispute", response);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Asynchronous thunk action to fetch dispute configuration data.
 *
 * @function
 * @name disputeGetConfig
 * @async
 * @param {Object} _ - Unused parameter.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected action with a value.
 * @returns {Promise<Object>} The processed dispute configuration data.
 * @throws Will throw an error if the request fails.
 */
export const disputeGetConfig = createAsyncThunk(
  "dispute/disputeGetConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get("Dispute/Config");
      console.log("API Response", response);

      // Check if response.data exists and is an array
      if (!Array.isArray(response.data)) {
        console.error("Unexpected response format:", response);
        throw new Error("Invalid response format");
      }

      // Process the response data
      const processedData = response.data.map((item) => {
        console.log("Processing item:", item);

        // Ensure item.forms is an array before mapping
        if (!Array.isArray(item.forms)) {
          console.error("Unexpected forms format:", item.forms);
          return { ...item, forms: [] }; // Handle gracefully
        }

        return {
          ...item,
          forms: item.forms.map((form) => {
            try {
              return {
                ...form,
                jsonSchema: JSON.parse(form.jsonSchema), // Parse JSON string
              };
            } catch (parseError) {
              console.error(
                "Error parsing jsonSchema:",
                form.jsonSchema,
                parseError
              );
              return form; // Return form without parsed jsonSchema in case of error
            }
          }),
        };
      });

      console.log("Processed Data", processedData);
      return processedData;
    } catch (error) {
      console.error("Error in disputeGetConfig:", error);
      return rejectWithValue(error.response?.data || "Unknown error");
    }
  }
);

// export const disputeGetConfig = createAsyncThunk(
//   "dispute/disputeGetConfig",
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await API.get(`Dispute/Config`);
//       console.log("dispute Config", response);

//       // Process the response data to parse jsonSchema strings
//       const processedData = response.data.map((item) => ({
//         ...item,
//         forms: item.forms.map((form) => ({
//           ...form,
//           jsonSchema: JSON.parse(form.jsonSchema),
//           // jsonSchema: JSON.parse(form.jsonSchema),
//         })),
//       }));
//       console.log("processedData", processedData);
//       return processedData;
//     } catch (error) {
//       return rejectWithValue(error.response.data);
//     }
//   }
// );

/**
 * Fetches dispute data from the server.
 *
 * @function
 * @name fetchDispute
 * @param {Object} params - The parameters for the request.
 * @param {Object} params.dataState - The state of the data to be fetched.
 * @param {number} params.dataState.skip - The number of records to skip.
 * @param {number} params.dataState.take - The number of records to take.
 * @param {Array} params.dataState.sort - The sorting criteria.
 * @param {Array} params.dataState.filter - The filtering criteria.
 * @param {Array} params.dataState.group - The grouping criteria.
 * @param {AbortController} params.controller - The abort controller to cancel the request.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - The function to reject with a value.
 * @returns {Promise<Object>} The fetched dispute data.
 * @throws Will throw an error if the request fails.
 */
export const fetchDispute = createAsyncThunk(
  "dispute/fetchDispute",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      const requestBody = {
        skip: dataState.skip,
        take: dataState.take,
        sort: dataState.sort.map((sort, index) => ({
          field: sort.field,
          dir: sort.dir,
          index: index,
        })),
        filter: {
          logic: dataState.filter.logic,
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
        group: dataState.group,
      };

      const response = await API.post(`Dispute/GetData`, requestBody, {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Asynchronous thunk action to fetch a specific dispute step.
 *
 * @function
 * @param {Object} params - The parameters for the API request.
 * @param {number} params.disputeTypeId - The ID of the dispute type.
 * @param {number} params.stepId - The ID of the step.
 * @param {number} params.reconRefId - The reference ID for the reconciliation.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value.
 * @returns {Promise<Object>} The data of the fetched dispute step.
 * @throws Will throw an error if the API request fails.
 */
export const getDisputeStep = createAsyncThunk(
  "dispute/getDisputeStep",
  async ({ disputeTypeId, stepId, reconRefId }, { rejectWithValue }) => {
    try {
      const response = await API.get(
        `Dispute/${disputeTypeId}/${reconRefId}/StepByID/${stepId}`
      );
      //console.log("byId", response);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk action to save dispute details.
 *
 * @function
 * @param {Object} payload - The data to be sent in the request body.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected action with a value.
 * @returns {Promise<Object>} The response data if the request is successful.
 * @throws {Object} The error response data if the request fails.
 */
export const disputeDetailSave = createAsyncThunk(
  "dispute/disputeDetailSave",
  async (payload, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.post(`Dispute/DetailsSave`, payload);
      notifySuccess("Detail saved Successfully!");
      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Fetches the audit log details by dispute ID.
 *
 * @function
 * @async
 * @param {string} disputeId - The ID of the dispute to fetch the audit log for.
 * @param {Object} thunkAPI - The thunk API object provided by createAsyncThunk.
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected action with a payload.
 * @returns {Promise<Object[]>} The parsed audit log details.
 * @throws Will throw an error if the request fails.
 */
export const fetchAuditLogById = createAsyncThunk(
  "dispute/fetchAuditLogById",
  async (disputeId, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.get(`AuditLog/GetById/${disputeId}`);
      // notifySuccess("Dispute Approved Successfully!");
      const data = response?.data;

      const parsedData = data?.map((detail) => ({
        ...detail,
        details: detail.details ? JSON.parse(detail.details) : {},
      }));

      console.log("Parsed data:", parsedData);
      // console.log('fetchDisputeDetails', response);
      return parsedData;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Acknowledges a dispute by its ID.
 *
 * @function
 * @name acknowledgeDispute
 * @param {string} disputeId - The ID of the dispute to acknowledge.
 * @param {object} thunkAPI - The thunk API object.
 * @param {function} thunkAPI.rejectWithValue - A function to return a rejected value.
 * @returns {Promise<object>} The response data from the API call.
 * @throws Will throw an error if the API call fails.
 */
export const acknowledgeDispute = createAsyncThunk(
  "dispute/acknowledgeDispute",
  async (disputeId, { rejectWithValue }) => {
    try {
      // const formData = new FormData();
      // formData.append("BusinessUnitName", businessUnitName);

      const response = await API.put(`Dispute/Acknowledgement/${disputeId}`);
      console.log("res", response);
      notifySuccess("Dispute Acknowledged Successfully!");

      // if (response.status === 200) {
      //   redirectComplainView();
      // }

      return response.data.data;
    } catch (error) {
      notifyError(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Fetches the audit log data based on the provided data state.
 *
 * @function
 * @name fetchAuditLog
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.dataState - The state of the data including pagination, sorting, and filtering.
 * @param {number} params.dataState.skip - The number of records to skip.
 * @param {number} params.dataState.take - The number of records to take.
 * @param {Array} [params.dataState.sort] - The sorting criteria.
 * @param {string} params.dataState.sort[].field - The field to sort by.
 * @param {string} params.dataState.sort[].dir - The direction of sorting (asc/desc).
 * @param {Object} [params.dataState.filter] - The filtering criteria.
 * @param {string} params.dataState.filter.logic - The logic operator for the filters (and/or).
 * @param {Array} params.dataState.filter.filters - The array of filter objects.
 * @param {string} params.dataState.filter.filters[].field - The field to filter by.
 * @param {string} params.dataState.filter.filters[].operator - The filter operator (e.g., eq, neq, lt, gt).
 * @param {string|number} params.dataState.filter.filters[].value - The value to filter by.
 * @param {AbortController} params.controller - The AbortController to handle request cancellation.
 * @param {Object} thunkAPI - The thunk API object provided by createAsyncThunk.
 * @param {Function} thunkAPI.rejectWithValue - The function to reject the thunk with a value.
 * @returns {Promise<Object>} The fetched audit log data.
 */
export const fetchAuditLog = createAsyncThunk(
  "dispute/fetchAuditLog",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      // Log dataState for debugging
      console.log("DataState:", dataState);

      // Construct request body based on dataState
      const requestBody = {
        skip: dataState.skip,
        take: dataState.take,
        sort:
          dataState.sort?.map((sort, index) => ({
            field: sort.field,
            dir: sort.dir,
            index: index,
          })) || [], // Handle no sorting case with empty array
        filter: dataState.filter
          ? {
              logic: dataState.filter.logic,
              filters: dataState.filter.filters.map(
                ({ field, operator, value }) => ({
                  field,
                  operator,
                  value,
                })
              ),
            }
          : null, // Handle no filters case with null
      };

      // Log requestBody for debugging
      console.log("RequestBody:", requestBody);

      // API call with request body
      const response = await API.post(`AuditLog/GetData`, requestBody, {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Return data
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetches debit authority data asynchronously.
 *
 * @function fetchDebitAuthority
 * @param {Object} params - The parameters for the fetch operation.
 * @param {Object} params.dataState - The state of the data including pagination, sorting, and filtering.
 * @param {number} params.dataState.skip - The number of records to skip.
 * @param {number} params.dataState.take - The number of records to take.
 * @param {Array} [params.dataState.sort] - The sorting criteria.
 * @param {string} params.dataState.sort[].field - The field to sort by.
 * @param {string} params.dataState.sort[].dir - The direction of sorting (asc/desc).
 * @param {Object} [params.dataState.filter] - The filtering criteria.
 * @param {string} params.dataState.filter.logic - The logical operator for the filters (and/or).
 * @param {Array} params.dataState.filter.filters - The array of filter objects.
 * @param {string} params.dataState.filter.filters[].field - The field to filter by.
 * @param {string} params.dataState.filter.filters[].operator - The filter operator (e.g., eq, neq, lt, gt).
 * @param {string|number} params.dataState.filter.filters[].value - The value to filter by.
 * @param {AbortController} params.controller - The AbortController to signal request cancellation.
 * @param {Object} thunkAPI - The thunk API object provided by createAsyncThunk.
 * @param {Function} thunkAPI.rejectWithValue - The function to reject the thunk with a value.
 * @returns {Promise<Object>} The fetched data or an error message.
 */
export const fetchDebitAuthority = createAsyncThunk(
  "dispute/fetchDebitAuthority",
  async ({ id, dataState, controller }, { rejectWithValue }) => {
    try {
      // Log dataState for debugging
      console.log("DataState:", dataState);

      // Construct request body based on dataState
      const requestBody = {
        skip: dataState.skip,
        take: dataState.take,
        sort:
          dataState.sort?.map((sort, index) => ({
            field: sort.field,
            dir: sort.dir,
            index: index,
          })) || [], // Handle no sorting case with empty array
        filter: dataState.filter
          ? {
              logic: dataState.filter.logic,
              filters: dataState.filter.filters.map(
                ({ field, operator, value }) => ({
                  field,
                  operator,
                  value,
                })
              ),
            }
          : null, // Handle no filters case with null
      };

      // Log requestBody for debugging
      console.log("RequestBody:", requestBody);

      // API call with request body
      const response = await API.post(
        `DADetail/GetDADetail/${id}`,
        requestBody,
        {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Return data
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDAFileData = createAsyncThunk(
  "dispute/fetchDAFileData",
  async ({ inStatus, dataState, controller }, { rejectWithValue }) => {
    try {
      const requestBody = {
        skip: dataState.skip,
        take: dataState.take,
        sort: dataState.sort.map((sort, index) => ({
          field: sort.field,
          dir: sort.dir,
          index: index,
        })),
        filter: {
          logic: dataState.filter.logic,
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
        group: dataState.group,
      };

      // API call with request body
      const response = await API.post(
        `DADetail/GetFileData/${inStatus}`,
        requestBody,
        {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Return data
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadDaFile = createAsyncThunk(
  "dispute/uploadDaFile",
  async ({ file, controller }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file); // Key name must match what the backend expects

      const response = await API.post(`DADetail/FileUpload`, formData, {
        signal: controller?.signal,
        headers: {
          "Content-Type": "multipart/form-data", // Axios will auto-set boundary
        },
      });
      notifySuccess(response?.data.message);

      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;

      notifyError(errorMsg); // Show notification

      return rejectWithValue(error.response?.data || errorMsg); // Return to thunk
    }
  }
);

export const downloadDaFile = createAsyncThunk(
  "dispute/downloadDaFile",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      // Format the dates in mm-dd-yyyy format
      const formattedStartDate = dayjs(startDate).format("MM-DD-YYYY");
      const formattedEndDate = dayjs(endDate).format("MM-DD-YYYY");

      // Send the formatted dates in the API request
      const response = await API.get(
        `DADetail/DownloadFile?startingDate=${formattedStartDate}&endingDate=${formattedEndDate}`
      );

      return response.data;
    } catch (error) {
      // Return the error message or response if available
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetches proactive dispute data based on the provided data state and controller.
 *
 * @function
 * @name fetchProActive
 * @param {Object} params - The parameters for the fetch operation.
 * @param {Object} params.dataState - The state of the data to be fetched.
 * @param {number} params.dataState.skip - The number of records to skip.
 * @param {number} params.dataState.take - The number of records to take.
 * @param {Array} params.dataState.sort - The sorting criteria.
 * @param {string} params.dataState.sort[].field - The field to sort by.
 * @param {string} params.dataState.sort[].dir - The direction to sort (asc/desc).
 * @param {Object} params.dataState.filter - The filtering criteria.
 * @param {string} params.dataState.filter.logic - The logical operator for filters (and/or).
 * @param {Array} params.dataState.filter.filters - The individual filters.
 * @param {string} params.dataState.filter.filters[].field - The field to filter by.
 * @param {string} params.dataState.filter.filters[].operator - The filter operator.
 * @param {string|number} params.dataState.filter.filters[].value - The filter value.
 * @param {Array} params.dataState.group - The grouping criteria.
 * @param {AbortController} params.controller - The abort controller to cancel the request if needed.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - The function to reject with a value.
 * @returns {Promise<Object>} The response data from the API.
 * @throws Will throw an error if the request fails.
 */
export const fetchProActive = createAsyncThunk(
  "dispute/fetchProActive",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      const requestBody = {
        skip: dataState.skip,
        take: dataState.take,
        sort: dataState.sort.map((sort, index) => ({
          field: sort.field,
          dir: sort.dir,
          index: index,
        })),
        filter: {
          logic: dataState.filter.logic,
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
        group: dataState.group,
      };

      const response = await API.post(`ReconReference/GetData`, requestBody, {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Fetches ATM balancing data.
 *
 * @function
 * @name fetchAtmBalancing
 * @param {Object} params - The parameters for the request.
 * @param {Object} params.dataState - The state of the data.
 * @param {number} params.dataState.skip - The number of records to skip.
 * @param {number} params.dataState.take - The number of records to take.
 * @param {Array} params.dataState.sort - The sorting criteria.
 * @param {string} params.dataState.sort[].field - The field to sort by.
 * @param {string} params.dataState.sort[].dir - The direction to sort (asc/desc).
 * @param {Object} params.dataState.filter - The filter criteria.
 * @param {string} params.dataState.filter.logic - The logic to apply to the filters (and/or).
 * @param {Array} params.dataState.filter.filters - The filters to apply.
 * @param {string} params.dataState.filter.filters[].field - The field to filter by.
 * @param {string} params.dataState.filter.filters[].operator - The operator to use for filtering.
 * @param {string|number} params.dataState.filter.filters[].value - The value to filter by.
 * @param {Array} params.dataState.group - The grouping criteria.
 * @param {AbortController} params.controller - The abort controller to cancel the request.
 * @param {Object} thunkAPI - The thunk API object.
 * @param {Function} thunkAPI.rejectWithValue - The function to reject with a value.
 * @returns {Promise<Object>} The response data.
 */
export const fetchAtmBalancing = createAsyncThunk(
  "dispute/fetchAtmBalancing",
  async ({ dataState, controller, selectedDate }, { rejectWithValue }) => {
    try {
      console.log("lll", dataState);

      const requestBody = {
        skip: dataState.skip,
        take: dataState.take,
        sort: dataState.sort.map((sort, index) => ({
          field: sort.field,
          dir: sort.dir,
          index: index,
        })),
        // filter: {},
        filter: {
          logic: dataState.filter.logic,
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
        group: dataState.group,
      };
      const queryParams = selectedDate ? `?CreatedDate=${selectedDate}` : "";
      const response = await API.post(
        `ATMBalancing/GetData${queryParams}`,
        requestBody,
        {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log("errrorrorororor", error);

      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchRegion = createAsyncThunk(
  "dispute/fetchRegion",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      const requestBody = {
        skip: dataState.skip,
        take: dataState.take,
        sort: dataState.sort.map((sort, index) => ({
          field: sort.field,
          dir: sort.dir,
          index: index,
        })),
        filter: {
          logic: dataState.filter.logic,
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
        group: dataState.group,
      };

      const response = await API.post(`Region/GetData`, requestBody, {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error) {
      // ✅ Correct way to call notifyError

      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCity = createAsyncThunk(
  "dispute/fetchCity",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      console.log("Fetching city data with:", dataState);

      const requestBody = {
        take: dataState.take || 0,
        skip: dataState.skip || 0,
        sort: dataState.sort.map(({ field, dir }) => ({
          field,
          dir,
        })),
        filter: {
          caseSensitive: true, // Defaulting to true; update as needed
          field: dataState.filter.filters[0]?.field || "", // Assuming the first filter
          operator: dataState.filter.filters[0]?.operator || "",
          value: dataState.filter.filters[0]?.value || "",
          valueConverted: dataState.filter.filters[0]?.value || "", // Assuming same value
          logic: dataState.filter.logic || "and",
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
      };

      console.log("Request Payload:", JSON.stringify(requestBody, null, 2));

      const response = await API.post(`City/GetData`, requestBody, {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("API Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);

      if (error.response) {
        console.error("Response Data:", error.response.data);
        console.error("Response Status:", error.response.status);
        console.error("Response Headers:", error.response.headers);
        return rejectWithValue(error.response.data);
      } else if (error.request) {
        console.error("No Response Received:", error.request);
      } else {
        console.error("Request Setup Error:", error.message);
      }

      return rejectWithValue(error.message || "Something went wrong");
    }
  }
);

export const fetchBank = createAsyncThunk(
  "dispute/fetchBank",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      console.log("Fetching city data with:", dataState);

      const requestBody = {
        take: dataState.take || 0,
        skip: dataState.skip || 0,
        sort: dataState.sort.map(({ field, dir }) => ({
          field,
          dir,
        })),
        filter: {
          caseSensitive: true, // Defaulting to true; update as needed
          field: dataState.filter.filters[0]?.field || "", // Assuming the first filter
          operator: dataState.filter.filters[0]?.operator || "",
          value: dataState.filter.filters[0]?.value || "",
          valueConverted: dataState.filter.filters[0]?.value || "", // Assuming same value
          logic: dataState.filter.logic || "and",
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
      };

      console.log("Request Payload:", JSON.stringify(requestBody, null, 2));

      const response = await API.post(`Bank/GetData`, requestBody, {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("API Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);

      if (error.response) {
        console.error("Response Data:", error.response.data);
        console.error("Response Status:", error.response.status);
        console.error("Response Headers:", error.response.headers);
        return rejectWithValue(error.response.data);
      } else if (error.request) {
        console.error("No Response Received:", error.request);
      } else {
        console.error("Request Setup Error:", error.message);
      }

      return rejectWithValue(error.message);
    }
  }
);

export const fetchBranch = createAsyncThunk(
  "dispute/fetchBranch",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      console.log("Fetching city data with:", dataState);

      const requestBody = {
        take: dataState.take || 0,
        skip: dataState.skip || 0,
        sort: dataState.sort.map(({ field, dir }) => ({
          field,
          dir,
        })),
        filter: {
          caseSensitive: true, // Defaulting to true; update as needed
          field: dataState.filter.filters[0]?.field || "", // Assuming the first filter
          operator: dataState.filter.filters[0]?.operator || "",
          value: dataState.filter.filters[0]?.value || "",
          valueConverted: dataState.filter.filters[0]?.value || "", // Assuming same value
          logic: dataState.filter.logic || "and",
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
      };

      console.log("Request Payload:", JSON.stringify(requestBody, null, 2));

      const response = await API.post(
        `ATMBalancing/GetData/Branches`,
        requestBody,
        {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("API Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);

      if (error.response) {
        console.error("Response Data:", error.response.data);
        console.error("Response Status:", error.response.status);
        console.error("Response Headers:", error.response.headers);
        return rejectWithValue(error.response.data);
      } else if (error.request) {
        console.error("No Response Received:", error.request);
      } else {
        console.error("Request Setup Error:", error.message);
      }

      return rejectWithValue(error.message);
    }
  }
);

export const fetchTerminal = createAsyncThunk(
  "dispute/fetchTerminal",
  async ({ dataState, controller }, { rejectWithValue }) => {
    try {
      console.log("Fetching city data with:", dataState);

      const requestBody = {
        take: dataState.take || 0,
        skip: dataState.skip || 0,
        sort: dataState.sort.map(({ field, dir }) => ({
          field,
          dir,
        })),
        filter: {
          caseSensitive: true, // Defaulting to true; update as needed
          field: dataState.filter.filters[0]?.field || "", // Assuming the first filter
          operator: dataState.filter.filters[0]?.operator || "",
          value: dataState.filter.filters[0]?.value || "",
          valueConverted: dataState.filter.filters[0]?.value || "", // Assuming same value
          logic: dataState.filter.logic || "and",
          filters: dataState.filter.filters.map(
            ({ field, operator, value }) => ({
              field,
              operator,
              value,
            })
          ),
        },
      };

      console.log("Request Payload:", JSON.stringify(requestBody, null, 2));

      const response = await API.post(
        `ATMBalancing/GetAllTerminals`,
        requestBody,
        {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("API Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);

      if (error.response) {
        console.error("Response Data:", error.response.data);
        console.error("Response Status:", error.response.status);
        console.error("Response Headers:", error.response.headers);
        return rejectWithValue(error.response.data);
      } else if (error.request) {
        console.error("No Response Received:", error.request);
      } else {
        console.error("Request Setup Error:", error.message);
      }

      return rejectWithValue(error.message);
    }
  }
);

export const getEscalationTime = createAsyncThunk(
  "recon/getEscalationTIme",
  async (_, { rejectWithValue }) => {
    // console.log('Bearer123', user.token);
    try {
      console.log("uilll3");

      const response = await API.get("Configuration");

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
